import React, { useState, useEffect, useRef } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { motion, AnimatePresence } from "framer-motion";

const FileInput = ({
    id,
    onChange,
    oldValue,
    disabled = false,
    aspect = 1,
    cropRequired = false, // New prop: if false, cropping is skipped
    className
}) => {
    // Holds the preview URL (original or cropped)
    const [preview, setPreview] = useState(oldValue || null);
    // Keeps track of the original object URL (so it can be revoked later)
    const [objectUrl, setObjectUrl] = useState(null);
    // Controls whether the cropping modal is open
    const [showModal, setShowModal] = useState(false);
    // Fixed crop (250×250) with a 1:1 aspect ratio (locked so only position can change)
    const [crop, setCrop] = useState({
        unit: "px",
        x: 0,
        y: 0,
        width: 250,
        height: 250,
        aspect: 1,
        minWidth: 200,
        maxWidth: 350,
        minHeight: 200,
        maxHeight: 350,
    });
    // This will be set when the crop is finalized (or when the image is loaded)
    const [completedCrop, setCompletedCrop] = useState(null);
    const imageRef = useRef(null);

    // If an old value (existing image) is provided, set it as preview
    useEffect(() => {
        if (!preview && oldValue) {
            setPreview(oldValue);
        }
    }, [oldValue, preview]);

    // When the user selects a file...
    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Revoke any previous object URL to avoid memory leaks
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        const newObjectUrl = URL.createObjectURL(file);
        setPreview(newObjectUrl);
        setObjectUrl(newObjectUrl);

        if (cropRequired) {
            // Open the crop modal and reset the crop state
            setShowModal(true);
            setCompletedCrop(null);
        } else {
            // No cropping needed: immediately pass the file upward
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);
            onChange({
                target: {
                    name: id,
                    files: dataTransfer.files,
                },
            });
        }
    };

    // onImageLoaded ensures that if the user does not interact with the crop,
    // we still have a valid crop based on our default crop state.
    const onImageLoaded = (img) => {
        imageRef.current = img;
        if (!completedCrop) {
            setCompletedCrop({ ...crop });
        }
        return false;
    };

    // This function creates a cropped image file from the selected crop area.
    const getCroppedImg = async () => {
        if (!imageRef.current || !completedCrop) return null;
        const image = imageRef.current;
        // Calculate the scaling factors between the displayed image and its natural size
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        // Create a canvas with the correct dimensions
        const canvas = document.createElement("canvas");
        canvas.width = completedCrop.width * scaleX;
        canvas.height = completedCrop.height * scaleY;
        const ctx = canvas.getContext("2d");

        // Create a circular clipping path so the cropped result is circular.
        // (If you do not need a circular crop, remove these lines.)
        ctx.save();
        ctx.beginPath();
        ctx.arc(
            (completedCrop.width * scaleX) / 2,
            (completedCrop.height * scaleY) / 2,
            (completedCrop.width * scaleX) / 2,
            0,
            2 * Math.PI
        );
        ctx.closePath();
        ctx.clip();

        // Draw the cropped area onto the canvas
        ctx.drawImage(
            image,
            completedCrop.x * scaleX,
            completedCrop.y * scaleY,
            completedCrop.width * scaleX,
            completedCrop.height * scaleY,
            0,
            0,
            canvas.width,
            canvas.height
        );
        ctx.restore();

        // Convert the canvas to a blob and then to a File
        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                if (!blob) return;
                const croppedFile = new File([blob], "cropped-profile.png", {
                    type: "image/png",
                    lastModified: Date.now(),
                });
                resolve(croppedFile);
            }, "image/png");
        });
    };

    // Called when the user clicks the Crop button in the modal.
    // It uses getCroppedImg to generate the new image, updates the preview,
    // and fires onChange (so that the parent can update its form data).
    const handleCropConfirm = async (e) => {
        e.preventDefault();
        // If for some reason completedCrop is still null, use the default crop
        if (!completedCrop) {
            setCompletedCrop({ ...crop });
        }
        const croppedFile = await getCroppedImg();
        if (croppedFile) {
            const croppedUrl = URL.createObjectURL(croppedFile);
            setPreview(croppedUrl);

            // Simulate an input event so that parent's handleChange is called
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(croppedFile);
            onChange({
                target: {
                    name: id,
                    files: dataTransfer.files,
                },
            });

            // Close the modal
            setShowModal(false);
        }
    };

    // Called when the user cancels cropping; resets the preview and notifies the parent.
    const handleCropCancel = () => {
        setShowModal(false);
        setPreview(null);
        onChange({
            target: {
                name: id,
                files: new DataTransfer().files,
            },
        });
    };

    return (
        <div className="flex flex-col items-center">
            {/* Main File Upload Area */}
            <div className={`flex items-center justify-center w-full ${className}`}>
                <motion.label
                    htmlFor={id}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    className="flex flex-col items-center justify-center w-full h-48 sm:h-64 border-2 border-dashed rounded-xl cursor-pointer bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 hover:from-blue-50 hover:to-blue-100 dark:hover:from-blue-900/20 dark:hover:to-blue-800/20 border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-300 overflow-hidden group relative"
                >
                    {/* If a preview exists and the modal isn’t open, display it */}
                    {preview && !showModal ? (
                        <motion.img
                            src={preview}
                            alt="Preview"
                            className="w-full h-full object-contain"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.3 }}
                        />
                    ) : (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex flex-col items-center justify-center p-2 z-10"
                        >
                            <motion.div
                                className="w-16 h-16 mb-4 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                                whileHover={{ rotate: 5, scale: 1.1 }}
                            >
                                <svg
                                    className="w-8 h-8 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                    />
                                </svg>
                            </motion.div>
                            <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                <span className="font-semibold">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                SVG, PNG, JPG or GIF (MAX. 800x400px)
                            </p>
                        </motion.div>
                    )}
                    <input
                        id={id}
                        type="file"
                        onChange={handleFileChange}
                        className="hidden"
                        disabled={disabled}
                        accept="image/*"
                    />
                </motion.label>
            </div>

            {/* Modal for cropping, shown only if cropRequired is true */}
            <AnimatePresence>
                {cropRequired && showModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                    >
                        <motion.div
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -50, opacity: 0 }}
                            className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-2xl max-w-md w-full relative border border-gray-200 dark:border-gray-700"
                        >
                            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Crop your image</h2>
                            <div className="w-full">
                                <ReactCrop
                                    crop={crop}
                                    onChange={(newCrop) =>
                                        setCrop((prevCrop) => ({
                                            ...prevCrop,
                                            x: newCrop.x,
                                            y: newCrop.y,
                                        }))
                                    }
                                    onComplete={(c) => setCompletedCrop(c)}
                                    onImageLoaded={onImageLoaded}
                                    aspect={aspect}
                                    circularCrop={true}
                                    locked
                                >
                                    <img
                                        ref={imageRef}
                                        src={preview}
                                        alt="To be cropped"
                                        className="w-full h-full object-contain"
                                    />
                                </ReactCrop>
                            </div>
                            <div className="flex justify-end mt-6 space-x-3">
                                <motion.button
                                    onClick={handleCropCancel}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="px-5 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
                                >
                                    Cancel
                                </motion.button>
                                <motion.button
                                    onClick={handleCropConfirm}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-medium shadow-lg"
                                >
                                    Crop
                                </motion.button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default FileInput;
